// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// protected: NO
// =============================================================================
// FASE 8C.2 — Master Diskon per Klub/Region.
// - Owner: kelola semua region. Kepala Cabang: HANYA klub/region-nya (backend region guard).
// - VALIDASI INSTAN: tombol Simpan NONAKTIF + pesan merah bila ETAMS% + Klub% != besaran diskon.
// - Nonaktifkan (bukan hapus). Snapshot transaksi tak berubah (dijaga backend Fase 8A/8B).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Percent, Plus, Pencil, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { isOwner } from "@/v2/components/helpers";

const CLUB_BY_REGION = { SBY: "Arema", SMD: "Borneo" };
const CLUBS = ["Arema", "Borneo"];
const REGION_BY_CLUB = { Arema: "SBY", Borneo: "SMD" };
const EPS = 0.001;

const emptyForm = () => ({ id: null, name: "", percent: "", etams_pct: "", club_pct: "", club: "", active: true });

export default function DiscountsPage() {
  const { api, me } = useV2Auth();
  const owner = isOwner(me?.role);
  const fixedClub = owner ? null : CLUB_BY_REGION[me?.region] || null; // Kacab: klub region-nya

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/discounts");
      if (r.ok) { const d = await r.json(); setRows(d.discounts || []); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Gagal memuat diskon"); }
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // --- VALIDASI INSTAN ---
  const pctN = Number(form.percent), eN = Number(form.etams_pct), cN = Number(form.club_pct);
  const numsFilled = form.percent !== "" && form.etams_pct !== "" && form.club_pct !== "";
  const numsValid = numsFilled && [pctN, eN, cN].every((x) => !isNaN(x) && x >= 0);
  const sumMatch = numsValid && Math.abs((eN + cN) - pctN) < EPS;
  const clubChosen = owner ? !!form.club : true;
  const nameOk = form.name.trim().length > 0;
  const canSave = nameOk && numsValid && sumMatch && clubChosen && !saving;

  const validationMsg = useMemo(() => {
    if (numsFilled && !numsValid) return "Persentase harus angka ≥ 0.";
    if (numsValid && !sumMatch)
      return `Beban ETAMS (${eN}%) + Klub (${cN}%) = ${(eN + cN)}% — TIDAK sama dengan besaran diskon (${pctN}%). Perbaiki agar tagihan ke klub benar.`;
    return "";
  }, [numsFilled, numsValid, sumMatch, eN, cN, pctN]);

  const autoFillClub = () => {
    if (form.percent !== "" && form.etams_pct !== "" && !isNaN(pctN) && !isNaN(eN))
      setField("club_pct", String(Math.max(0, +(pctN - eN).toFixed(2))));
  };

  const openCreate = () => { setForm({ ...emptyForm(), club: fixedClub || "" }); setDialogOpen(true); };
  const openEdit = (d) => {
    setForm({ id: d.id, name: d.name, percent: String(d.percent), etams_pct: String(d.etams_pct),
              club_pct: String(d.club_pct), club: d.club, active: d.active });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const club = owner ? form.club : fixedClub;
      let r;
      if (form.id) {
        r = await api(`/api/v2/discounts/${form.id}`, { method: "PUT", body: JSON.stringify({
          name: form.name.trim(), percent: pctN, etams_pct: eN, club_pct: cN, active: form.active }) });
      } else {
        r = await api("/api/v2/discounts", { method: "POST", body: JSON.stringify({
          name: form.name.trim(), percent: pctN, etams_pct: eN, club_pct: cN, club, active: form.active }) });
      }
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menyimpan diskon"); return; }
      toast.success(`Diskon "${form.name.trim()}" disimpan`);
      setDialogOpen(false);
      await load();
    } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    const r = await api(`/api/v2/discounts/${d.id}`, { method: "PUT", body: JSON.stringify({ active: !d.active }) });
    const dd = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(typeof dd.detail === "string" ? dd.detail : "Gagal mengubah status"); return; }
    toast.success(`Diskon "${d.name}" ${!d.active ? "diaktifkan" : "dinonaktifkan"}`);
    await load();
  };

  return (
    <div className="space-y-5" data-testid="v2-discounts-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Percent className="w-6 h-6 text-primary" /> Master Diskon
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {owner ? "Kelola diskon semua region." : `Kelola diskon klub ${fixedClub || "-"} (region Anda).`}
            {" "}Beban ETAMS% + Klub% wajib sama dengan besaran diskon.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="v2-disc-add">
          <Plus className="w-4 h-4 mr-1" /> Tambah Diskon
        </Button>
      </div>

      {/* Desktop table */}
      <Card className="p-5 rounded-2xl hidden md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nama</TableHead><TableHead>Klub / Region</TableHead>
            <TableHead className="text-right">Diskon</TableHead>
            <TableHead className="text-right">ETAMS</TableHead>
            <TableHead className="text-right">Klub</TableHead>
            <TableHead>Status</TableHead><TableHead className="w-40">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">Belum ada diskon. Klik "Tambah Diskon".</TableCell></TableRow>}
            {!loading && rows.map((d) => (
              <TableRow key={d.id} data-testid="v2-disc-row">
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell><Badge variant="secondary">{d.club}</Badge> <span className="text-xs text-muted-foreground">{d.region}</span></TableCell>
                <TableCell className="text-right font-semibold" data-testid="v2-disc-percent">{d.percent}%</TableCell>
                <TableCell className="text-right">{d.etams_pct}%</TableCell>
                <TableCell className="text-right">{d.club_pct}%</TableCell>
                <TableCell>
                  {d.active
                    ? <Badge variant="success" data-testid="v2-disc-status">Aktif</Badge>
                    : <Badge variant="outline" className="text-muted-foreground" data-testid="v2-disc-status">Nonaktif</Badge>}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid="v2-disc-edit"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant={d.active ? "ghost" : "secondary"} onClick={() => toggleActive(d)} data-testid="v2-disc-toggle">{d.active ? "Nonaktifkan" : "Aktifkan"}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="v2-disc-cards">
        {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {!loading && rows.length === 0 && <div className="text-center text-muted-foreground py-6 text-sm rounded-xl border border-dashed">Belum ada diskon.</div>}
        {!loading && rows.map((d) => (
          <div key={d.id} className="rounded-xl border p-3 space-y-2" data-testid="v2-disc-row-m">
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium">{d.name}</div>
              {d.active
                ? <Badge variant="success">Aktif</Badge>
                : <Badge variant="outline" className="text-muted-foreground">Nonaktif</Badge>}
            </div>
            <div className="text-xs text-muted-foreground"><Badge variant="secondary" className="mr-1">{d.club}</Badge>{d.region}</div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><div className="text-[11px] text-muted-foreground">Diskon</div><div className="font-semibold">{d.percent}%</div></div>
              <div><div className="text-[11px] text-muted-foreground">ETAMS</div><div>{d.etams_pct}%</div></div>
              <div><div className="text-[11px] text-muted-foreground">Klub</div><div>{d.club_pct}%</div></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(d)} data-testid="v2-disc-edit-m"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant={d.active ? "ghost" : "secondary"} className="flex-1" onClick={() => toggleActive(d)} data-testid="v2-disc-toggle-m">{d.active ? "Nonaktifkan" : "Aktifkan"}</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="v2-disc-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Diskon" : "Tambah Diskon"}</DialogTitle>
            <DialogDescription>Beban ETAMS% + Klub% harus sama dengan besaran diskon.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nama Diskon</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="mis. Diskon Member 15%" data-testid="v2-disc-name" />
            </div>
            <div>
              <Label className="text-xs">Klub</Label>
              {owner && !form.id ? (
                <Select value={form.club} onValueChange={(v) => setField("club", v)}>
                  <SelectTrigger data-testid="v2-disc-club"><SelectValue placeholder="Pilih klub" /></SelectTrigger>
                  <SelectContent>{CLUBS.map((c) => <SelectItem key={c} value={c}>{c} ({REGION_BY_CLUB[c]})</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 text-sm" data-testid="v2-disc-club-fixed">
                  {form.club || fixedClub} <span className="text-muted-foreground ml-1">({REGION_BY_CLUB[form.club || fixedClub]})</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Diskon %</Label>
                <Input type="number" min="0" value={form.percent} onChange={(e) => setField("percent", e.target.value)} data-testid="v2-disc-in-percent" />
              </div>
              <div>
                <Label className="text-xs">ETAMS %</Label>
                <Input type="number" min="0" value={form.etams_pct} onChange={(e) => setField("etams_pct", e.target.value)} data-testid="v2-disc-in-etams" />
              </div>
              <div>
                <Label className="text-xs">Klub %</Label>
                <Input type="number" min="0" value={form.club_pct} onChange={(e) => setField("club_pct", e.target.value)} data-testid="v2-disc-in-club" />
              </div>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={autoFillClub} data-testid="v2-disc-autofill" className="h-7 text-xs">
              Isi otomatis Klub% = Diskon − ETAMS
            </Button>

            {/* VALIDASI INSTAN */}
            {validationMsg ? (
              <div className="flex items-start gap-2 text-sm rounded-lg border border-destructive/50 bg-destructive/10 text-destructive px-3 py-2" data-testid="v2-disc-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{validationMsg}</span>
              </div>
            ) : (numsValid && (
              <div className="flex items-center gap-2 text-sm rounded-lg border border-[hsl(var(--badge-success))]/40 bg-[hsl(var(--badge-success))]/10 text-[hsl(var(--badge-success))] px-3 py-2" data-testid="v2-disc-ok">
                <CheckCircle2 className="w-4 h-4 shrink-0" /><span>Pembagian beban valid: {eN}% + {cN}% = {pctN}%.</span>
              </div>
            ))}

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => setField("active", v)} data-testid="v2-disc-active" />
              <span className="text-sm">Aktif (bisa dipakai kasir)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="v2-disc-cancel">Batal</Button>
            <Button onClick={submit} disabled={!canSave} data-testid="v2-disc-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
